import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip, CircularProgress, Dialog, DialogActions, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { Refresh as RefreshIcon } from '@material-ui/icons';

import { GetProofOfIdResponse, QueryStrings, ReservationDetails } from '../../../types';
import { getProofOfId, resetDocumentValidation, useMutateApiData, useQueryApiData } from '../../../util/api';
import { ConfirmDialog, ErrorMessage } from '../../common';
import TextInputField from '../../common/section/TextInputField';
import S3File from './S3File';
import { getCheckinStatus } from '../checkin-status.utils';
import environmentConfig from '../../../config';

type Props = Pick<ReservationDetails, 'reservation'>;

const useStyles = makeStyles({
  proofOfIdWrapper: {
    display: 'grid',
    gap: '20px',
  },
  fileGrid: {
    marginTop: '20px',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: '10px',
  },
  dialogTrigger: {
    border: 'none',
    padding: '0',
    background: 'transparent',
    cursor: 'pointer',
  },
  resetDialogContent: {
    display: 'grid',
    gap: '10px',
  },
  resetDialogText: {
    margin: 0,
  },
  resetDialogError: {
    margin: 0,
  },
  tableCell: {
    padding: '8px',
  },
});

const documentStatusDictionary: Record<GetProofOfIdResponse['guests'][number]['documentStatus'], string> = {
  missing: 'Missing',
  approved: 'Approved',
  incode_approved: 'Approved (by Incode)',
  incode_manual_review_approved: 'Approved (manual review)',
  incode_manual_review: 'Manual review pending',
};

export const ProofOfId = ({ reservation }: Props) => {
  const classes = useStyles();
  const reservationId = reservation.id;
  const { isLoading, isSuccess, data, refetch } = useQueryApiData<GetProofOfIdResponse>(
    [reservationId, QueryStrings.PROOF_OF_ID],
    getProofOfId,
  );
  const manualIdUploadUrl = reservation.urls?.manualIdUpload;
  const { t } = useTranslation();

  const [openedFile, setOpenedFile] = useState<string | undefined>(undefined);
  const [guestToResetId, setGuestToResetId] = useState<string | undefined>(undefined);
  const closeDialog = () => setOpenedFile(undefined);
  const openResetDialog = (guest: GetProofOfIdResponse['guests'][number]) => setGuestToResetId(guest.id);
  const {
    isLoading: isResetting,
    error: resetError,
    mutateAsync: resetValidationRequest,
  } = useMutateApiData(resetDocumentValidation, async () => {
    await refetch();
  });

  const guestToReset = guestToResetId ? data?.guests.find((guest) => guest.id === guestToResetId) : undefined;

  const getGuestDisplayName = (guest: GetProofOfIdResponse['guests'][number]) => {
    return guest.presentationName || `Guest ${guest.orderIndex + 1}`;
  };

  const renderGuestTypeChip = (guest: GetProofOfIdResponse['guests'][number]) => {
    const isPrimary = guest.type === 'primaryGuest';
    return (
      <Chip
        label={isPrimary ? 'Primary' : 'Co-Guest'}
        color={isPrimary ? 'primary' : 'secondary'}
        size="small"
        variant="outlined"
        style={{ fontSize: '0.7rem', height: '20px' }}
      />
    );
  };

  const handleResetConfirm = async (accepted: boolean): Promise<void> => {
    if (!accepted) {
      setGuestToResetId(undefined);
      return;
    }

    if (!guestToResetId) {
      return;
    }

    try {
      await resetValidationRequest([reservationId, guestToResetId]);
      setGuestToResetId(undefined);
    } catch (error) {
      // Keep dialog open so the user can retry or cancel
    }
  };

  const renderStatus = (guest: GetProofOfIdResponse['guests'][number]) => {
    const status = documentStatusDictionary[guest.documentStatus];
    const incodeUrl = environmentConfig.incodeUrl;

    if (incodeUrl && guest.incodeSessionId) {
      return (
        <a href={`${incodeUrl}/single-session/${guest.incodeSessionId}`} target="_blank" rel="noopener noreferrer">
          {status}
        </a>
      );
    }

    return status;
  };

  return (
    <div className={classes.proofOfIdWrapper}>
      {isLoading && <CircularProgress />}

      <div>
        <b>Online check-in Status:</b> {getCheckinStatus(reservation)}
      </div>

      {isSuccess && (
        <>
          <TableContainer>
            <Table aria-labelledby="guest-proof-of-id" size="medium">
              <TableHead>
                <TableRow>
                  <TableCell align="left" className={classes.tableCell}>Guest Type</TableCell>
                  <TableCell align="left" className={classes.tableCell}>Guest Name</TableCell>
                  <TableCell align="left" className={classes.tableCell}>Document Status</TableCell>
                  <TableCell align="center" className={classes.tableCell}>Reset</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.guests.map((guest) => {
                  return (
                    <TableRow key={guest.id} hover>
                      <TableCell align="left" className={classes.tableCell}>
                        {renderGuestTypeChip(guest)}
                      </TableCell>
                      <TableCell align="left" className={classes.tableCell}>
                        <strong>{getGuestDisplayName(guest)}</strong>
                      </TableCell>
                      <TableCell align="left" className={classes.tableCell}>
                        {renderStatus(guest)}
                      </TableCell>
                      <TableCell align="center" className={classes.tableCell}>
                        <Button
                          variant="outlined"
                          color="secondary"
                          size="small"
                          onClick={() => openResetDialog(guest)}
                          disabled={isResetting}
                          aria-label={t('proofOfId.resetValidation')}
                        >
                          <RefreshIcon />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <div>
            <strong>Additional ID uploads</strong>
            {/* eslint-disable-next-line @typescript-eslint/no-empty-function */}
            {manualIdUploadUrl && <TextInputField label="Link to Upload page" value={manualIdUploadUrl} onChange={() => {}} />}

            <div className={classes.fileGrid}>
              {data?.reservationDocuments?.map((document) => (
                <S3File
                  key={document.downloadUrl}
                  downloadUrl={document.downloadUrl}
                  isPreview
                  onClick={() => setOpenedFile(document.downloadUrl)}
                />
              ))}
            </div>

            {openedFile && (
              <Dialog open={!!openedFile} onClose={closeDialog}>
                <S3File downloadUrl={openedFile} />

                <DialogActions>
                  <Button onClick={closeDialog} variant="contained" color="primary">
                    {t('general.ok')}
                  </Button>
                </DialogActions>
              </Dialog>
            )}
          </div>
        </>
      )}

      {guestToReset && (
        <ConfirmDialog
          open
          confirmTitle={t('proofOfId.resetValidationConfirmTitle')}
          confirmText={
            <div className={classes.resetDialogContent}>
              <p className={classes.resetDialogText}>
                {t('proofOfId.resetValidationConfirmText', {
                  guestName: getGuestDisplayName(guestToReset),
                  guestId: guestToReset.id,
                })}
              </p>
              {resetError && <ErrorMessage className={classes.resetDialogError} message={t('general.error')} />}
            </div>
          }
          onClose={handleResetConfirm}
          loading={isResetting}
        />
      )}
    </div>
  );
};
